import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Hospital, 
  Building2, 
  Users, 
  Package, 
  Settings,
  LogOut,
  Activity,
  ChevronRight
} from 'lucide-react';
import { BASE_URL } from '../config';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = parseInt(localStorage.getItem('userRole'));
  const [sidebarPermissions, setSidebarPermissions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch permissions for Admin role
  useEffect(() => {
    const fetchSidebarPermissions = async () => {
      try {
        const userId = localStorage.getItem('userId');
        const userRole = localStorage.getItem('userRole');
        
        console.log('🔍 Sidebar - Fetching permissions for user:', userId, 'role:', userRole);
        
        // If superadmin (role 6), grant all permissions
        if (userRole === '6') {
          console.log('✅ Sidebar - Superadmin detected - granting all permissions');
          setSidebarPermissions({
            dashboard: true,
            hospitals: true,
            clinics: true,
            doctors: true,
            users: true,
            packages: true,
            departments: true,
            seo: true,
          });
          setLoading(false);
          return;
        }
        
        // For admin (role 5), fetch from API
        if (userRole === '5' && userId) {
          console.log('🔍 Sidebar - Admin detected - fetching permissions from API');
          const res = await axios.get(`${BASE_URL}/api/superadmin/permissions/${userId}/`);
          console.log('✅ Sidebar - Permissions fetched:', res.data.sidebar_access);
          setSidebarPermissions(res.data.sidebar_access || {});
        } else {
          // For other roles, set null (use default menu)
          setSidebarPermissions(null);
        }
      } catch (err) {
        console.error('❌ Sidebar - Error fetching permissions:', err);
        setSidebarPermissions(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSidebarPermissions();
  }, []);

  const allMenuItems = {
    6: [ // Superadmin
      { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/superadmin_dashboard' },
      { key: 'hospitals', icon: Hospital, label: 'Hospitals', path: '/hospitals_dashboard' },
      { key: 'clinics', icon: Building2, label: 'Clinics', path: '/clinic_dashboard' },
      { key: 'doctors', icon: Users, label: 'Doctors', path: '/doctors' },
      { key: 'users', icon: Users, label: 'Users', path: '/user' },
      { key: 'packages', icon: Package, label: 'Packages', path: '/create_package' },
      { key: 'departments', icon: Settings, label: 'Departments', path: '/create_department' },
      { key: 'seo', icon: Settings, label: 'SEO Management', path: '/seo' },
    ],
    5: [ // Admin - will be filtered by permissions
      { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
      { key: 'hospitals', icon: Hospital, label: 'Hospitals', path: '/hospitals_dashboard' },
      { key: 'clinics', icon: Building2, label: 'Clinics', path: '/clinic_dashboard' },
      { key: 'doctors', icon: Users, label: 'Doctors', path: '/doctors' },
      { key: 'users', icon: Users, label: 'Users', path: '/user' },
      { key: 'packages', icon: Package, label: 'Packages', path: '/create_package' },
      { key: 'departments', icon: Settings, label: 'Departments', path: '/create_department' },
      { key: 'seo', icon: Settings, label: 'SEO Management', path: '/seo' },
    ],
    4: [ // Hospital
      { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/hospitals' },
      { key: 'doctors', icon: Users, label: 'Doctors', path: '/add_doctors' },
      { key: 'package_details', icon: Package, label: 'Package Details', path: '/package_details' },
      { key: 'addon_credits', icon: Package, label: 'Add-On Credits', path: '/addon_credits' },
    ],
    3: [ // Clinic
      { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/clinic' },
      { key: 'doctors', icon: Users, label: 'Doctors', path: '/clinic_dashboard/add_doctors' },
      { key: 'package_details', icon: Package, label: 'Package Details', path: '/clinic_package_details' },
      { key: 'addon_credits', icon: Package, label: 'Add-On Credits', path: '/clinic_addon_credits' },
    ],
  };

  // Filter menu items based on permissions for Admin role
  const getFilteredMenu = () => {
    const baseMenu = allMenuItems[role] || [];
    
    // If not admin or permissions not loaded yet, return base menu
    if (role !== 5 || !sidebarPermissions) {
      return baseMenu;
    }
    
    // Filter menu items based on permissions
    const filteredMenu = baseMenu.filter(item => {
      // Dashboard is always shown
      if (item.key === 'dashboard') return true;
      
      // Check if permission exists and is true
      return sidebarPermissions[item.key] === true;
    });
    
    console.log('🔍 Sidebar - Filtered menu:', filteredMenu.map(m => m.label));
    return filteredMenu;
  };

  const currentMenu = getFilteredMenu();

  const handleLogout = () => {
    localStorage.clear();
    navigate('/', { replace: true });
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-200 flex flex-col z-40">
      {/* Logo Section */}
      <div className="h-16 flex items-center px-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center ">
            <img src="/logo-h100.png" alt="ClinicalAssist" className="h-10" />
          </div>
          {/* <div>
            <h1 className="text-base font-semibold text-slate-900">ClinicalAssist AI</h1>
            <p className="text-xs text-slate-600">Healthcare System</p>
          </div> */}
        </div>
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {loading && role === 5 ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <div className="space-y-1">
            {currentMenu.map((item, index) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm ${
                    isActive
                      ? 'bg-primary-500 text-white shadow-lg'
                      : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left font-medium">{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="p-3 border-t border-slate-200">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
            <span className="text-slate-900 text-sm font-semibold">
              {localStorage.getItem('userName')?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">
              {localStorage.getItem('userName') || 'User'}
            </p>
            <p className="text-xs text-slate-600">
              {role === 6 ? 'Superadmin' : role === 5 ? 'Admin' : role === 4 ? 'Hospital' : 'Clinic'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all text-sm"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </aside>
  );
}
